import { useMapStore } from '@/stores/map.store';
import { onBeforeUnmount, watch, type Ref } from 'vue';

let activeOwner: symbol | null = null;

/**
 * Observes a bottom-drawer element's height and pushes it to
 * `mapStore.bottomDrawerHeight` so map controls can shift above the drawer.
 * Cleans up automatically on unmount or when the element is removed.
 *
 * Uses a global owner token so that when two drawers swap in the same
 * render cycle, the outgoing drawer only resets the height if it is
 * still the active owner.
 */
export function useDrawerHeight(elRef: Ref<HTMLElement | null>) {
  const mapStore = useMapStore();
  const token = Symbol();
  let observer: ResizeObserver | null = null;

  function attach(el: HTMLElement) {
    activeOwner = token;
    mapStore.bottomDrawerHeight = el.offsetHeight;
    observer = new ResizeObserver((entries) => {
      if (activeOwner !== token) return;
      const height = entries[0]?.borderBoxSize?.[0]?.blockSize ?? entries[0]?.contentRect.height ?? 0;
      mapStore.bottomDrawerHeight = height;
    });
    observer.observe(el, { box: 'border-box' });
  }

  function detach() {
    observer?.disconnect();
    observer = null;
    if (activeOwner === token) {
      activeOwner = null;
      mapStore.bottomDrawerHeight = 0;
    }
  }

  watch(
    elRef,
    (el, oldEl) => {
      if (oldEl || (!el && observer)) detach();
      if (el) attach(el);
    },
    { immediate: true },
  );

  onBeforeUnmount(detach);
}
